import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import useRBAC from "../hooks/useRBAC";
import { CalendarClock, Download, Layers, ListChecks, Package, Plus, Settings } from "lucide-react";
import GrcControlWorkflow from "./GrcControlWorkflow";
import { SegmentedTabs } from "./TabStrips";
import { btn } from "../utils/buttonStyles";
import {
  AddToPlanForm, ApplicabilityForm, AssetStateForm, DecideForm, EvaluationForm, ExportForm, ImplementForm, PlanForm, ProgressForm, ReviseForm,
  SettingsForm, SourceForm, StatementForm, VendorForm, forgetVendorNames,
} from "./NercPatchForms";
import { RetireForm } from "./NercAssetForms";
import { CLOCK_LABELS, apiError, formatDateOnly } from "../utils/nercAssets";
import {
  APPROVAL_LABELS, DUE_ITEM_LABELS, OUTCOME_LABELS, PATCH_STATE_LABELS, REQUIREMENT_LABELS, STATEMENT_REASON_LABELS, VERSION_KIND_LABELS,
  pendingStates, pendingWithinDecision,
} from "../utils/nercPatches";

/**
 * GRC → Assets → NERC sites → Patch management (point 4, CIP-007 R2): patch sources for the in-scope
 * Cyber Assets (High / Medium impact systems and their EACMS, PACS, PCA), evaluations every 35 days with
 * their outcome and evidence, applicable patches (one per vendor + identifier, whichever sources found
 * them) with each asset's state and its own 35-day decision date, mitigation plans whose revisions and
 * extensions need the Senior Manager's or a delegate's approval, and what is due. Each applicable patch
 * that still needs action has its own risk, worked here with the Risks page's buttons (Jira, ServiceNow,
 * Assign…). Closing that risk never resolves the patch. The server checks every action and says what
 * each person may do (canEdit / canDecide); dates are the organisation's NERC calendar days.
 */

const VIEWS = [
  { key: "due", label: "Due", icon: CalendarClock },
  { key: "sources", label: "Sources", icon: Layers },
  { key: "patches", label: "Patches", icon: Package },
  { key: "plans", label: "Plans", icon: ListChecks },
];

const Chip = ({ label }) => label
  ? <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold whitespace-nowrap ${label.tone}`}>{label.text}</span>
  : null;

const PATCH_RISKS_URL = "/unifiedrisks/details?riskTypes=GRC&riskSources=NercPatch";

const linkBtn = btn("secondary", "xs");

export default function NercPatchPanel({ organizationId }) {
  const apiCall = useApi();
  const { canManageSecurityInsights } = useRBAC();
  const canManage = canManageSecurityInsights(organizationId);

  const [view, setView] = useState("due");
  const [overview, setOverview] = useState(null);
  const [patches, setPatches] = useState(null);
  const [plans, setPlans] = useState(null);
  const [risks, setRisks] = useState({});
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [openPatch, setOpenPatch] = useState(null);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    forgetVendorNames();
    try {
      const [o, p, pl] = await Promise.all([
        apiCall("GET", "/nerc-patches"),
        apiCall("GET", "/nerc-patches/patches"),
        apiCall("GET", "/nerc-patches/plans"),
      ]);
      setOverview(o); setPatches(p || []); setPlans(pl || []);
    } catch (err) {
      setError(apiError(err, "Could not load patch management."));
    }
    // The patches' own risks only (never the whole GRC register), for the workflow buttons.
    apiCall("GET", PATCH_RISKS_URL)
      .then((list) => setRisks(Object.fromEntries((Array.isArray(list) ? list : []).map((r) => [String(r.id), r]))))
      .catch(() => setRisks({}));
  }, [apiCall]);

  useEffect(() => { load(); }, [load, organizationId]);

  useEffect(() => {
    if (!canManage) return;
    apiCall("GET", "/users").then((res) => setUsers(res || [])).catch(() => setUsers([]));
  }, [apiCall, canManage]);

  const saved = () => { setDialog(null); load(); };
  const close = () => setDialog(null);
  const sourcesById = useMemo(() => Object.fromEntries((overview?.sources || []).map((s) => [s.id, s])), [overview]);
  const patchesById = useMemo(() => Object.fromEntries((patches || []).map((p) => [p.id, p])), [patches]);
  const today = overview?.settings?.today;
  const inScope = (overview?.assets || []).filter((a) => a.inScope);
  const withoutSource = inScope.filter((a) => !a.meetsR21);
  const shownPatches = (patches || []).filter((p) => showClosed || !p.closed);
  const openPlans = (plans || []).filter((pl) => pl.state === "Open" && pl.canEdit && !pl.revisionPending);

  const pairOptions = (patch, states) => states.map((a) => ({ patchId: patch.id, cyberAssetId: a.cyberAssetId, label: `${patch.identifier} on ${a.assetName}` }));
  const confirm = async (source) => {
    try { await apiCall("POST", `/nerc-patches/sources/${source.id}/confirm`); load(); }
    catch (err) { setError(apiError(err, "Could not confirm the source.")); }
  };

  if (error && !overview) return <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{error}</p>;
  if (!overview) return <p className="text-sm text-gray-500">Loading patch management…</p>;
  const settings = overview.settings;

  return (
    <div className="space-y-4" data-testid="nerc-patch-panel">
      <div className="flex flex-wrap items-start gap-2">
        <p className="text-xs text-gray-500 flex-1 min-w-[16rem]">
          CIP-007 R2 for {inScope.length} in-scope Cyber Asset{inScope.length === 1 ? "" : "s"} (High and Medium impact systems and their EACMS, PACS and PCA):
          identify patch sources, evaluate them every 35 days, and apply each applicable patch or put it under a mitigation plan within 35 days.
          {settings && <span className="block">Assessed against {settings.cip007Version} · days counted in {settings.timeZoneId}.</span>}
        </p>
        <button type="button" className={btn("neutral")} onClick={() => setDialog({ kind: "export" })} data-testid="nerc-patch-export"><Download className="h-4 w-4" />Export evidence</button>
        {canManage && <button type="button" className={btn("neutral")} onClick={() => setDialog({ kind: "settings" })} data-testid="nerc-patch-settings"><Settings className="h-4 w-4" />Settings</button>}
      </div>

      {settings && !settings.timeZoneSet && (
        <p className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2" data-testid="nerc-patch-time-zone-notice">
          The NERC time zone is not set, so every 35-day clock counts UTC days.
          {canManage ? <> <button type="button" className={btn("secondary", "xs")} onClick={() => setDialog({ kind: "settings" })}>Set the time zone</button>.</> : " Ask someone who manages Security Insights to set it."}
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="nerc-patch-readiness">
        {overview.readiness.map((r) => (
          <div key={r.requirement} className="rounded-lg border border-gray-200 bg-white px-3 py-2" data-testid={`nerc-patch-share-${r.requirement}`}>
            <div className="text-xs text-gray-500">{r.requirement} · {REQUIREMENT_LABELS[r.requirement]}</div>
            <div className={`text-lg font-semibold ${r.status === "Implemented" ? "text-green-700" : "text-red-700"}`}>
              {r.status === "Implemented" ? "Met" : "Not met"}
              <span className="ml-2 text-xs font-normal text-gray-500">{r.total === 0 ? "nothing open" : `${r.met} / ${r.total}`}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SegmentedTabs ariaLabel="Patch management view" value={view} onChange={setView}
          items={VIEWS.map((v) => (v.key === "due" ? { ...v, badge: overview.due.length } : v))} />
        <div className="flex-1" />
        {canManage && view === "sources" && (
          <button type="button" onClick={() => setDialog({ kind: "source" })} className={btn("primary")}><Plus className="h-4 w-4" />Add patch source</button>
        )}
      </div>

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{error}</p>}

      {/* ── Due ── */}
      {view === "due" && (
        overview.due.length === 0
          ? <p className="text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center" data-testid="nerc-patch-nothing-due">Nothing is overdue or due in the next 7 days.</p>
          : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="nerc-patch-due">
                <thead className="bg-gray-50"><tr className="text-left text-xs text-gray-500"><th className="px-4 py-2">What</th><th>Item</th><th>Due</th><th>Status</th><th>Owner</th></tr></thead>
                <tbody>
                  {overview.due.map((d) => (
                    <tr key={`${d.itemType}-${d.itemId}`} className="border-t border-gray-100">
                      <td className="px-4 py-2 text-xs text-gray-500">{DUE_ITEM_LABELS[d.itemType] || d.itemType}</td>
                      <td>{d.title}</td>
                      <td>{formatDateOnly(d.dueOn)}</td>
                      <td>{d.status === "Pending" ? <Chip label={{ text: "Waiting for approval", tone: "bg-blue-50 text-blue-700 border-blue-200" }} /> : <Chip label={CLOCK_LABELS[d.status]} />}</td>
                      <td>{d.ownerName || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* ── Sources ── */}
      {view === "sources" && (
        <div className="space-y-3">
          {overview.sources.length === 0
            ? <p className="text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">No patch sources yet. {canManage ? "Add one for each vendor or product line your in-scope assets get patches from." : "Someone who manages Security Insights can add them."}</p>
            : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="nerc-patch-sources">
                  <thead className="bg-gray-50"><tr className="text-left text-xs text-gray-500"><th className="px-4 py-2">Source</th><th>Owner</th><th>Assets in scope</th><th>Last evaluated</th><th>Next due</th><th>Status</th><th /></tr></thead>
                  <tbody>
                    {overview.sources.map((s) => (
                      <tr key={s.id} className={`border-t border-gray-100 ${s.retiredAt ? "opacity-60" : ""}`} data-testid={`nerc-patch-source-${s.id}`}>
                        <td className="px-4 py-2 font-medium">
                          {s.name}
                          <span className="block text-xs text-gray-500 font-normal">{[s.vendor, s.product].filter(Boolean).join(" · ")}</span>
                          <span className="block text-[11px] text-gray-500 font-normal" data-testid={`nerc-patch-source-confirmed-${s.id}`}>
                            Coverage confirmed {formatDateOnly(s.confirmedOn)}{s.confirmedByName ? ` by ${s.confirmedByName}` : ""}
                          </span>
                        </td>
                        <td>{s.ownerName || "—"}</td>
                        <td>{s.inScopeAssets}</td>
                        <td>
                          {formatDateOnly(s.lastEvaluatedOn)}
                          {s.lastUnsuccessfulOn && (
                            <span className="block text-[11px] text-orange-700" data-testid={`nerc-patch-source-unsuccessful-${s.id}`}>
                              Last attempt {formatDateOnly(s.lastUnsuccessfulOn)}: {OUTCOME_LABELS[s.lastUnsuccessfulOutcome] || s.lastUnsuccessfulOutcome}
                            </span>
                          )}
                        </td>
                        <td>{s.retiredAt ? "—" : formatDateOnly(s.dueOn)}</td>
                        <td>{s.retiredAt ? <span className="text-xs text-gray-400">retired</span> : <Chip label={CLOCK_LABELS[s.clockStatus]} />}</td>
                        <td className="px-3 text-right whitespace-nowrap">
                          {s.canEdit && <button type="button" className={`${linkBtn} mr-2`} onClick={() => setDialog({ kind: "evaluate", source: s })} data-testid={`nerc-patch-evaluate-${s.id}`}>Evaluate</button>}
                          {canManage && !s.retiredAt && <button type="button" className={`${linkBtn} mr-2`} onClick={() => confirm(s)} data-testid={`nerc-patch-confirm-${s.id}`}>Confirm coverage</button>}
                          {canManage && !s.retiredAt && <button type="button" className={`${linkBtn} mr-2`} onClick={() => setDialog({ kind: "source", source: s })}>Edit</button>}
                          {canManage && !s.retiredAt && <button type="button" className={btn("danger", "xs")} onClick={() => setDialog({ kind: "retireSource", source: s })}>Retire</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

          <div className="bg-white border border-gray-200 rounded-xl p-3" data-testid="nerc-patch-without-source">
            <div className="text-xs font-semibold text-gray-600 mb-2">In-scope assets without a patch source ({withoutSource.length})</div>
            {withoutSource.length === 0
              ? <p className="text-xs text-gray-500">Every in-scope asset has a patch source or a &quot;no patch source&quot; statement (CIP-007 R2.1).</p>
              : (
                <ul className="space-y-1">
                  {withoutSource.map((a) => (
                    <li key={a.id} className="text-sm flex items-center gap-2">
                      <span>{a.name} <span className="text-xs text-gray-500">{a.siteName} · {a.classification}</span></span>
                      {canManage && <button type="button" className={linkBtn} onClick={() => setDialog({ kind: "statement", asset: a })}>No patch source…</button>}
                    </li>
                  ))}
                </ul>
              )}
            {inScope.some((a) => a.statementId) && (
              <div className="mt-2 text-xs text-gray-600">
                {inScope.filter((a) => a.statementId).map((a) => (
                  <div key={a.id}>
                    {a.name}: {(STATEMENT_REASON_LABELS[a.statementReasonType] || "No patch source").toLowerCase()} — {a.statementReason}
                    {canManage && <button type="button" className={`ml-2 ${btn("danger", "xs")}`} onClick={async () => {
                      try { await apiCall("POST", `/nerc-patches/no-source-statements/${a.statementId}/retire`); load(); }
                      catch (err) { setError(apiError(err, "Could not withdraw the statement.")); }
                    }}>Withdraw</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Patches ── */}
      {view === "patches" && (
        <div className="space-y-2">
          <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} /> Show closed and not applicable</label>
          {shownPatches.length === 0 && <p className="text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">No open patches. Evaluate a source to record patches.</p>}
          {shownPatches.map((p) => {
            const open = openPatch === p.id;
            const risk = p.unifiedRiskId ? risks[String(p.unifiedRiskId)] : null;
            const newPlanOptions = pairOptions(p, pendingStates(p));
            const addOptions = pairOptions(p, pendingWithinDecision(p, today));
            const sources = [...new Set((p.observations || []).map((o) => o.sourceName))];
            const duplicateOf = p.possibleDuplicateOfPatchId ? patchesById[p.possibleDuplicateOfPatchId] : null;
            return (
              <div key={p.id} className="bg-white border border-gray-200 rounded-xl" data-testid={`nerc-patch-${p.id}`}>
                <button type="button" className="w-full text-left px-4 py-2 flex flex-wrap items-center gap-x-4 gap-y-1" onClick={() => setOpenPatch(open ? null : p.id)} aria-expanded={open}>
                  <span className="font-medium text-sm flex-1 min-w-[14rem]">
                    {p.vendor} {p.identifier} — {p.title}
                    <span className="block text-xs text-gray-500 font-normal">
                      {sources.join(", ") || p.sourceName} · first evaluated {formatDateOnly(p.evaluatedOn)}{p.cveIds?.length ? ` · ${p.cveIds.join(", ")}` : ""}
                    </span>
                    {p.possibleDuplicateOfPatchId && (
                      <span className="block text-[11px] text-orange-700 font-normal" data-testid={`nerc-patch-duplicate-${p.id}`}>
                        Possible duplicate of {duplicateOf ? `${duplicateOf.vendor} ${duplicateOf.identifier}` : "an earlier patch"} (recorded twice before patches were matched by vendor and identifier).
                      </span>
                    )}
                  </span>
                  {p.applicability === "NotApplicable"
                    ? <span className="text-xs text-gray-500">Not applicable</span>
                    : p.closed ? <Chip label={{ text: "Closed", tone: "bg-green-50 text-green-700 border-green-200" }} />
                      : p.decisionDueOn
                        ? <span className="text-xs">Next decision by {formatDateOnly(p.decisionDueOn)} <Chip label={CLOCK_LABELS[p.decisionStatus]} /></span>
                        : <span className="text-xs">Under a plan</span>}
                </button>
                {open && (
                  <div className="px-4 pb-3 space-y-3" data-testid={`nerc-patch-detail-${p.id}`}>
                    {p.reason && <p className="text-xs text-gray-600">Not applicable: {p.reason}</p>}
                    {p.products?.length > 0 && <p className="text-xs text-gray-600">Products: {p.products.join(", ")}</p>}
                    {(p.observations || []).length > 1 && (
                      <p className="text-xs text-gray-600" data-testid={`nerc-patch-observations-${p.id}`}>
                        Found by {p.observations.map((o) => `${o.sourceName} on ${formatDateOnly(o.observedOn)}${o.applicability !== p.applicability ? ` (said ${o.applicability === "Applicable" ? "applicable" : "not applicable"})` : ""}`).join("; ")}.
                      </p>
                    )}
                    {p.assets.length > 0 && (
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-gray-500"><th>Asset</th><th>State</th><th>Decide by</th><th>Date</th><th>Notes</th><th /></tr></thead>
                        <tbody>
                          {p.assets.map((a) => (
                            <tr key={a.cyberAssetId} className={`border-t border-gray-100 ${a.inScope ? "" : "opacity-60"}`} data-testid={`nerc-patch-${p.id}-asset-${a.cyberAssetId}`}>
                              <td className="py-1">{a.assetName}{!a.inScope && <span className="ml-1 text-[10px] uppercase text-gray-400">out of scope</span>}</td>
                              <td><Chip label={PATCH_STATE_LABELS[a.state]} />{a.decisionBreached && <span className="ml-1 text-[11px] text-red-700 font-semibold">late</span>}</td>
                              <td className="text-xs">{formatDateOnly(a.decisionDueOn)}</td>
                              <td>{formatDateOnly(a.stateOn)}</td>
                              <td className="text-xs text-gray-600">
                                {a.reason}
                                {a.evidenceReference && <span className="block text-gray-500">Evidence: {a.evidenceReference}</span>}
                                {a.defenderStillReports && <span className="block text-red-700 font-semibold" data-testid={`nerc-patch-defender-warning-${a.cyberAssetId}`}>Defender still reports {p.cveIds.join(" or ")} on this asset.</span>}
                              </td>
                              <td className="text-right">
                                {p.canEdit && a.inScope && !p.closed && a.state !== "Mitigated" && (
                                  <button type="button" className={linkBtn} onClick={() => setDialog({ kind: "state", patch: p, asset: a })}>Update</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {p.canEdit && newPlanOptions.length > 0 && (
                        <button type="button" className={btn("secondary", "xs")}
                          onClick={() => setDialog({ kind: "plan", options: newPlanOptions })} data-testid={`nerc-patch-plan-${p.id}`}>Put under a new mitigation plan</button>
                      )}
                      {addOptions.length > 0 && openPlans.length > 0 && (
                        <button type="button" className={btn("secondary", "xs")}
                          onClick={() => setDialog({ kind: "addToPlan", options: addOptions })} data-testid={`nerc-patch-add-to-plan-${p.id}`}>Add to an existing plan</button>
                      )}
                      {canManage && (
                        <button type="button" className={btn("neutral", "xs")}
                          onClick={() => setDialog({ kind: "vendor", patch: p })} data-testid={`nerc-patch-vendor-${p.id}`}>Edit vendor</button>
                      )}
                      {p.canEdit && (
                        <button type="button" className={btn("neutral", "xs")}
                          onClick={() => setDialog({ kind: "applicability", patch: p })} data-testid={`nerc-patch-applicability-${p.id}`}>
                          {p.applicability === "Applicable" ? "Mark not applicable" : "Mark applicable"}
                        </button>
                      )}
                    </div>
                    {p.previousUnifiedRiskId && !p.closed && (
                      <p className="text-[11px] text-gray-500" data-testid={`nerc-patch-replaced-risk-${p.id}`}>
                        This patch had an earlier risk, now closed; this one replaces it. Closing a patch&apos;s risk does not complete the patch: record each asset here.
                      </p>
                    )}
                    {risk && <GrcControlWorkflow risks={[risk]} onChanged={load} className="border-t border-gray-100 pt-3" />}
                    {!risk && p.unifiedRiskId && !p.closed && <p className="text-xs text-gray-500">The risk for this patch is not visible to you.</p>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Plans ── */}
      {view === "plans" && (
        <div className="space-y-2">
          {(plans || []).length === 0 && <p className="text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">No mitigation plans. Open a patch and put its pending assets under a plan.</p>}
          {(plans || []).map((pl) => (
            <div key={pl.id} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`nerc-patch-plan-card-${pl.id}`}>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-medium text-sm flex-1">{pl.title}<span className="block text-xs text-gray-500 font-normal">Owner {pl.ownerName || "—"} · {pl.states.length} patch × asset</span></span>
                {pl.state === "Implemented"
                  ? <span className="text-xs text-green-700">Implemented {formatDateOnly(pl.implementedOn)}{pl.implementedEvidence ? ` · ${pl.implementedEvidence}` : ""}</span>
                  : <span className="text-xs">Complete by {formatDateOnly(pl.timeframeOn)}{pl.overdue && <span className="ml-1 text-red-700 font-semibold">overdue</span>}</span>}
                {pl.canEdit && !pl.revisionPending && <button type="button" className={linkBtn} onClick={() => setDialog({ kind: "progress", plan: pl })}>Record progress</button>}
                {pl.canEdit && !pl.revisionPending && <button type="button" className={linkBtn} onClick={() => setDialog({ kind: "revise", plan: pl })}>Request revision</button>}
                {pl.canEdit && <button type="button" className={linkBtn} onClick={() => setDialog({ kind: "implement", plan: pl })}>Mark implemented</button>}
              </div>
              {pl.state === "Open" && pl.countsForR24 === false && (
                <p className="mt-1 text-xs text-gray-500" data-testid={`nerc-patch-plan-not-counted-${pl.id}`}>
                  Every patch in this plan is now not applicable: it no longer counts for CIP-007 R2.4 and gets no reminders. Mark it implemented to close it.
                </p>
              )}
              {pl.breaches?.length > 0 && (
                <ul className="mt-2 text-xs text-red-700 space-y-0.5" data-testid={`nerc-patch-plan-breaches-${pl.id}`}>
                  {pl.breaches.map((b) => (
                    <li key={b.version}>Timeframe {formatDateOnly(b.timeframeOn)} (v{b.version}) passed while the plan was open, until {formatDateOnly(b.until)}.</li>
                  ))}
                </ul>
              )}
              <ol className="mt-2 space-y-1">
                {pl.versions.map((v) => (
                  <li key={v.version} className="text-xs text-gray-700 border-l-2 border-gray-200 pl-2">
                    <span className="font-semibold">v{v.version}</span> · {VERSION_KIND_LABELS[v.kind] || v.kind} · {formatDateOnly(v.timeframeOn)}{v.timeframeEvent ? ` (${v.timeframeEvent})` : ""} · {APPROVAL_LABELS[v.approvalState]}
                    {v.effectiveOn && ` from ${formatDateOnly(v.effectiveOn)}`}
                    {v.requestedByName && ` · requested by ${v.requestedByName}`}
                    {v.decidedByName && ` · decided by ${v.decidedByName}${v.decisionCapacity === "Delegate" ? " (delegate)" : ""}`}
                    {v.decisionNote && ` — ${v.decisionNote}`}
                    <span className="block text-gray-500 whitespace-pre-wrap">{v.actions}</span>
                    {v.completedActions && <span className="block text-green-800 whitespace-pre-wrap">Done: {v.completedActions}</span>}
                    {v.reasonNotApplied && v.kind === "Initial" && <span className="block text-gray-500">Why not applied: {v.reasonNotApplied}</span>}
                    {v.canDecide && (
                      <button type="button" className={`mt-1 ${btn("primary", "xs")}`}
                        onClick={() => setDialog({ kind: "decide", plan: pl, version: v })} data-testid={`nerc-patch-decide-${pl.id}`}>
                        Decide {v.isExtension ? "extension" : "revision"}
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {dialog?.kind === "settings" && <SettingsForm settings={settings} onClose={close} onSaved={saved} />}
      {dialog?.kind === "export" && (
        <ExportForm today={today} onClose={close}
          sites={[...new Map(overview.assets.map((a) => [a.siteId, { id: a.siteId, name: a.siteName }])).values()]} />
      )}
      {dialog?.kind === "source" && <SourceForm source={dialog.source} assets={overview.assets} users={users} onClose={close} onSaved={saved} />}
      {dialog?.kind === "retireSource" && (
        <RetireForm kind="patch source" item={dialog.source} endpoint={`/nerc-patches/sources/${dialog.source.id}/retire`} onClose={close} onSaved={saved} />
      )}
      {dialog?.kind === "statement" && <StatementForm asset={dialog.asset} onClose={close} onSaved={saved} />}
      {dialog?.kind === "evaluate" && <EvaluationForm source={sourcesById[dialog.source.id] || dialog.source} today={today} onClose={close} onSaved={saved} />}
      {dialog?.kind === "state" && <AssetStateForm patch={dialog.patch} asset={dialog.asset} today={today} onClose={close} onSaved={saved} />}
      {dialog?.kind === "vendor" && <VendorForm patch={dialog.patch} onClose={close} onSaved={saved} />}
      {dialog?.kind === "applicability" &&<ApplicabilityForm patch={dialog.patch} today={today} onClose={close} onSaved={saved} />}
      {dialog?.kind === "plan" && <PlanForm options={dialog.options} users={users} today={today} onClose={close} onSaved={saved} />}
      {dialog?.kind === "addToPlan" && <AddToPlanForm options={dialog.options} plans={openPlans} onClose={close} onSaved={saved} />}
      {dialog?.kind === "progress" && <ProgressForm plan={dialog.plan} onClose={close} onSaved={saved} />}
      {dialog?.kind === "revise" && <ReviseForm plan={dialog.plan} today={today} onClose={close} onSaved={saved} />}
      {dialog?.kind === "decide" && <DecideForm plan={dialog.plan} version={dialog.version} onClose={close} onSaved={saved} />}
      {dialog?.kind === "implement" && <ImplementForm plan={dialog.plan} today={today} onClose={close} onSaved={saved} />}
    </div>
  );
}
